import React, { useEffect, useMemo, useRef, useState } from "react";
import { ROUTES } from "../../../constants/routes-path";
import { vehicleService } from "../../../services/vehicleService";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  LightMode,
  Select,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure
} from "@chakra-ui/react";
import CardHeader from "../../../components/Card/CardHeader";
import Loading from "../../../components/Loading/Loading";
import {
  AddIcon,
  EditIcon,
  SearchIcon
} from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import {
  checkErrors,
  hasError,
  isValidCEP,
  isValidVehiclePlate,
  plateMask
} from "../../../utils/forms-utils";
import {
  fetchAddressByCEP,
  formatCEP,
  formatDocument,
  formatPhoneNumber, parseBoolean, ufs,
  validateCpfOrCnpj
} from "../../../utils";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import { FileAttachmentInput } from "../../../components/FileAttachmentInput/FileAttachmentInput";
import WebcamCapture from "../../../components/WebcamCapture/WebcamCapture";
import { GenderList, permitteesService } from "../../../services/permitteesService";
import SearchSelect from "../../../components/SearchSelect/SearchSelect";
import PermitteesTaxiStandModal from "./PermiteesTaxiStand/PermitteesTaxiStandModal";
import { permitteesPointsService, PointTypeList as pointTypeList } from "../../../services/permitteesPointsService";
import YearMaskedInput from "../../../components/YearInput/YearMaskedInput";
import { blobToFile } from "../ParkingCredentials/Documents/ParkingCredentialsUtils";
import moment from "moment";
import "moment/locale/pt-br";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

const PermitteesNew = ({initialData = null, loading = false}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const textColor = useColorModeValue("gray.700", "white");
  const cardSpacing = "20px";
  const formSpacing = "30px";
  const translationKey = "permittees";
  const formTranslationKey = `${translationKey}.form`;
  const formDirection = {sm: "column", md: "row"};
  const [formData, setFormData] = useState(undefined);
  const [inputError, setInputError] = useState({});
  const dialogRef = useRef(undefined);
  const attachmentRef = useRef(undefined);
  const [fileAttachmentName, setAttachmentFileName] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [selectedStand, setSelectedStand] = useState(null);
  const [vehicleType, setVehicleType] = useState(null);
  const [pracas, setPracas] = useState({});
  const { isOpen: isOpenStand, onOpen: onOpenStand, onClose: onCloseStand } = useDisclosure();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copy_id = searchParams.get('copy_id');
  const isEdit = Boolean(id);
  const isCopy = Boolean(copy_id);
  const [initialAttachmentFile, setInitialAttachmentFile] = useState(null);

  const handleImageCaptured = (imgBase64) => {
    setCroppedImage(imgBase64);
  };

  const [ownerAddress, setOwnerAddress] = useState({
    logradouro: "",
    bairro: "",
    localidade: "",
    uf: "",
  });

  const [defenderAddress, setDefenderAddress] = useState({
    logradouro: "",
    bairro: "",
    localidade: "",
    uf: "",
  });

  const genderList = useMemo(
    () => [
      GenderList.MALE,
      GenderList.FEMALE,
      GenderList.OTHER
    ], []);

  const ids = useMemo(() => ({
    placaVeiculo: "vehicle-plate",
    ufPlaca: "vehicle-uf-plate",
    veiculoAnoFab: "vehicle-fabrication-year",
    veiculoAnoModelo: "vehicle-model-year",
    veiculoMarcaModelo: "vehicle-model",
    corVeiculo: "vehicle-color",
    chassiVeiculo: "vehicle-chassis",
    renavamVeiculo: "vehicle-renavam",
    marcaTaximetro: "taximeter-brand",
    modeloTaximetro: "taximeter-model",
    numeroTaximetro: "taximeter-number",
    marcaTacografo: "tachograph-brand",
    modeloTacografo: "tachograph-model",
    numeroTacografo: "tachograph-number",
    pracaTaxi: "taxi-stand",
    pontoTaxi: "taxi-point",
    numero: "number",
    inscricaoMunicipal: "city-registration-number",
    dataInicioAtividade: "activity-initial-date",
    atividadeRemunerada: "paid-activity",
    motivoEdicao: "reason-update",
    foto: "photo",
    nomeProprietario: "owner-name",
    nomeSocialProprietario: "owner-social-name",
    apelidoProprietario: "owner-alias",
    sexoProprietario: "owner-gender",
    dataNascProprietario: "owner-birth",
    identidadeProprietario: "owner-id",
    orgaoExpedidorIdentidadeProprietario: "owner-id-expeditioner",
    ufIdentidadeProprietario: "owner-id-uf",
    cpfCnpjProprietario: "owner-cpf-cnpj",
    cnhProprietario: "owner-cnh",
    cnhProprietarioCategoria: "owner-cnh-category",
    cnhProprietarioValidade: "owner-cnh-valid",
    cepProprietario: "owner-cep",
    logradouroProprietario: "owner-street",
    logradouroNumeroProprietario: "owner-street-number",
    logradouroMunicipioProprietario: "owner-address-city",
    logradouroEstadoProprietario: "owner-address-state",
    telefoneFixoProprietario: "owner-landline",
    telefoneCelularProprietario: "owner-cellphone",
    logradouroBairroProprietario: "owner-address-neighbourhood",
    nomeDefensor: "defender-name",
    nomeSocialDefensor: "defender-social-name",
    apelidoDefensor: "defender-alias",
    sexoDefensor: "defender-gender",
    dataNascDefensor: "defender-birth",
    identidadeDefensor: "defender-id",
    orgaoExpedidorIdentidadeDefensor: "defender-id-expeditioner",
    ufIdentidadeDefensor: "defender-id-uf",
    cpfCnpjDefensor: "defender-cpf-cnpj",
    cnhDefensor: "defender-cnh",
    cnhDefensorCategoria: "defender-cnh-category",
    cnhDefensorValidade: "defender-cnh-valid",
    cepDefensor: "defender-cep",
    logradouroDefensor: "defender-street",
    logradouroNumeroDefensor: "defender-street-number",
    logradouroMunicipioDefensor: "defender-address-city",
    logradouroEstadoDefensor: "defender-address-state",
    telefoneFixoDefensor: "defender-landline",
    telefoneCelularDefensor: "defender-cellphone",
    logradouroBairroDefensor: "defender-address-neighbour",
    observacoes: "observations"
  }), []);

  const initFormData = (initialData) => {
    return {
      [ids.placaVeiculo]: {
        value: initialData?.placa_veiculo || "",
        validations: [],
      },
      [ids.ufPlaca]: {
        value: initialData?.uf_placa_veiculo || "",
        validations: [],
      },
      [ids.veiculoAnoModelo]: {
        value: initialData?.ano_modelo_veiculo || "",
        validations: [],
      },
      [ids.veiculoAnoFab]: {
        value: initialData?.ano_fabricacao_veiculo || "",
        validations: [],
      },
      [ids.veiculoMarcaModelo]: {
        value: initialData?.descricao_marca_modelo_veiculo || "",
        validations: [],
      },
      [ids.corVeiculo]: {
        value: initialData?.descricao_cor_veiculo || "",
        validations: [],
      },
      [ids.renavamVeiculo]: {
        value: initialData?.numero_renavam_veiculo || "",
        validations: [],
      },
      [ids.chassiVeiculo]: {
        value: initialData?.numero_chassi_veiculo || "",
        validations: [],
      },
      [ids.marcaTaximetro]: {
        value: initialData?.marca_taximetro || "",
        validations: [],
      },
      [ids.modeloTaximetro]: {
        value: initialData?.modelo_taximetro || "",
        validations: [],
      },
      [ids.numeroTaximetro]: {
        value: initialData?.numero_taximetro || "",
        validations: [],
      },
      [ids.marcaTacografo]: {
        value: initialData?.marca_tacografo || "",
        validations: [],
      },
      [ids.modeloTacografo]: {
        value: initialData?.modelo_tacografo || "",
        validations: [],
      },
      [ids.numeroTacografo]: {
        value: initialData?.numero_tacografo || "",
        validations: [],
      },
      [ids.pracaTaxi]: {
        value: parseInt(initialData?.id_ponto_permissionario, 10) || null,
        validations: [{ required: true }],
      },
      [ids.pontoTaxi]: {
        value: initialData?.numero || "",
        validations: [],
      },
      [ids.inscricaoMunicipal]: {
        value: initialData?.inscricao_municipal || "",
        validations: [],
      },
      [ids.dataInicioAtividade]: {
        value: initialData?.data_inicio_atividade?.split('/').reverse().join('-') || "",
        validations: [{ required: true }],
      },
      [ids.atividadeRemunerada]: {
        value: parseBoolean(initialData?.atividade_remunerada) ?? false,
        validations: [],
      },
      [ids.observacoes]: {
        value: initialData?.observacoes || "",
        validations: [],
      },
      [ids.motivoEdicao]: {
        value: initialData?.historico || "",
        validations: [{required: (isEdit)}],
      },
      [ids.nomeProprietario]: {
        value: initialData?.nome_proprietario || "",
        validations: [{ required: true }],
      },
      [ids.nomeSocialProprietario]: {
        value: initialData?.nome_social_proprietario || "",
        validations: [],
      },
      [ids.apelidoProprietario]: {
        value: initialData?.apelido_proprietario || "",
        validations: [],
      },
      [ids.sexoProprietario]: {
        value: initialData?.sexo_proprietario || "",
        validations: [],
      },
      [ids.dataNascProprietario]: {
        value: initialData?.data_nascimento_proprietario?.split('/').reverse().join('-') || "",
        validations: [{ required: true }],
      },
      [ids.identidadeProprietario]: {
        value: initialData?.identidade_proprietario || "",
        validations: [],
      },
      [ids.orgaoExpedidorIdentidadeProprietario]: {
        value: initialData?.orgao_expedidor_identidade_proprietario || "",
        validations: [],
      },
      [ids.ufIdentidadeProprietario]: {
        value: initialData?.uf_identidade_proprietario || "",
        validations: [],
      },
      [ids.cpfCnpjProprietario]: {
        value: initialData?.cpf_cnpj_proprietario || "",
        validations: [{ required: true }],
      },
      [ids.cnhProprietario]: {
        value: initialData?.cnh_proprietario || "",
        validations: [],
      },
      [ids.cnhProprietarioCategoria]: {
        value: initialData?.categoria_cnh_proprietario || "",
        validations: [],
      },
      [ids.cnhProprietarioValidade]: {
        value: initialData?.validade_cnh_proprietario || "",
        validations: [],
      },
      [ids.cepProprietario]: {
        value: initialData?.cep_proprietario || "",
        validations: [{ required: true }],
      },
      [ids.logradouroProprietario]: {
        value: initialData?.logradouro_proprietario || "",
        validations: [{ required: true }],
      },
      [ids.logradouroNumeroProprietario]: {
        value: initialData?.numero_proprietario || "",
        validations: [{ required: true }],
      },
      [ids.logradouroBairroProprietario]: {
        value: initialData?.bairro_proprietario || "",
        validations: [{required: true}],
      },
      [ids.logradouroMunicipioProprietario]: {
        value: initialData?.municipio_proprietario || "",
        validations: [{required: true}],
      },
      [ids.logradouroEstadoProprietario]: {
        value: initialData?.uf_proprietario || "",
        validations: [{required: true}],
      },
      [ids.telefoneFixoProprietario]: {
        value: initialData?.telefone_fixo_proprietario || "",
        validations: [],
      },
      [ids.telefoneCelularProprietario]: {
        value: initialData?.telefone_celular_proprietario || "",
        validations: [],
      },
      [ids.nomeDefensor]: {
        value: initialData?.nome_defensor || "",
        validations: [],
      },
      [ids.nomeSocialDefensor]: {
        value: initialData?.nome_social_defensor || "",
        validations: [],
      },
      [ids.apelidoDefensor]: {
        value: initialData?.apelido_defensor || "",
        validations: [],
      },
      [ids.sexoDefensor]: {
        value: initialData?.sexo_defensor || "",
        validations: [],
      },
      [ids.dataNascDefensor]: {
        value: initialData?.data_nascimento_defensor?.split('/').reverse().join('-') || "",
        validations: [],
      },
      [ids.identidadeDefensor]: {
        value: initialData?.identidade_defensor || "",
        validations: [],
      },
      [ids.orgaoExpedidorIdentidadeDefensor]: {
        value: initialData?.orgao_expedidor_identidade_defensor || "",
        validations: [],
      },
      [ids.ufIdentidadeDefensor]: {
        value: initialData?.uf_identidade_defensor || "",
        validations: [],
      },
      [ids.cpfCnpjDefensor]: {
        value: initialData?.cpf_cnpj_defensor || "",
        validations: [],
      },
      [ids.cnhDefensor]: {
        value: initialData?.cnh_defensor || "",
        validations: [],
      },
      [ids.cnhDefensorCategoria]: {
        value: initialData?.categoria_cnh_defensor || "",
        validations: [],
      },
      [ids.cnhDefensorValidade]: {
        value: initialData?.validade_cnh_defensor || "",
        validations: [],
      },
      [ids.cepDefensor]: {
        value: initialData?.cep_defensor || "",
        validations: [],
      },
      [ids.logradouroDefensor]: {
        value: initialData?.logradouro_defensor || "",
        validations: [],
      },
      [ids.logradouroNumeroDefensor]: {
        value: initialData?.numero_defensor || "",
        validations: [],
      },
      [ids.logradouroBairroDefensor]: {
        value: initialData?.bairro_defensor || "",
        validations: [],
      },
      [ids.logradouroMunicipioDefensor]: {
        value: initialData?.municipio_defensor || "",
        validations: [],
      },
      [ids.logradouroEstadoDefensor]: {
        value: initialData?.uf_defensor || "",
        validations: [],
      },
      [ids.telefoneFixoDefensor]: {
        value: initialData?.telefone_fixo_defensor || "",
        validations: [],
      },
      [ids.telefoneCelularDefensor]: {
        value: initialData?.telefone_celular_defensor || "",
        validations: [],
      },
    };
  };

  const maskMap = {
    [ids.placaVeiculo]: plateMask,
    [ids.cpfCnpjProprietario]: formatDocument,
    [ids.cpfCnpjDefensor]: formatDocument,
    [ids.telefoneFixoProprietario]: formatPhoneNumber,
    [ids.telefoneFixoDefensor]: formatPhoneNumber,
    [ids.telefoneCelularProprietario]: formatPhoneNumber,
    [ids.telefoneCelularDefensor]: formatPhoneNumber,
    [ids.cepProprietario]: formatCEP,
    [ids.cepDefensor]: formatCEP,
  };

  const validatorMap = {
    [ids.placaVeiculo]: {
      validate: (v) => !v || isValidVehiclePlate(v),
      errorKey: 'vehicle-plate-format',
    },
    [ids.cpfCnpjProprietario]: {
      validate: validateCpfOrCnpj,
      errorKey: 'cpf-cnpj-format',
    },
    [ids.cpfCnpjDefensor]: {
      validate: validateCpfOrCnpj,
      errorKey: 'cpf-cnpj-format',
    },
    [ids.telefoneFixoProprietario]: {
      validate: (v) => v.length >= 15 || v.length === 0,
      errorKey: 'phone-number-invalid',
    },
    [ids.telefoneFixoDefensor]: {
      validate: (v) => v.length >= 15 || v.length === 0,
      errorKey: 'phone-number-invalid',
    },
    [ids.telefoneCelularProprietario]: {
      validate: (v) => v.length >= 15 || v.length === 0,
      errorKey: 'phone-number-invalid',
    },
    [ids.telefoneCelularDefensor]: {
      validate: (v) => v.length >= 15 || v.length === 0,
      errorKey: 'phone-number-invalid',
    },
    [ids.cepProprietario]: {
      validate: isValidCEP,
      errorKey: 'cep-format',
    },
    [ids.cepDefensor]: {
      validate: isValidCEP,
      errorKey: 'cep-format',
    },
  };

  const handleCEPChange = async (e) => {
    handleInputChange(e);
    const formatted = formatCEP(e.target.value);
    const isOwner = e.target.id === ids.cepProprietario;

    if (formatted.length === 9) {
      const address = await fetchAddressByCEP(
        formatted.replace("-", ""),
        isOwner ? setOwnerAddress : setDefenderAddress
      );

      if (address) {
        let newFormData = {};
        if (isOwner) {
          newFormData = {
            ...formData,
            [ids.cepProprietario]: {
              value: formatted,
              validations: [{required: true}],
            },
            [ids.logradouroProprietario]: {
              value: address?.logradouro ?? "",
              validations: [{required: true}],
            },
            [ids.logradouroBairroProprietario]: {
              value: address?.bairro ?? "",
              validations: [{required: true}],
            },
            [ids.logradouroMunicipioProprietario]: {
              value: address?.localidade ?? "",
              validations: [{required: true}],
            },
            [ids.logradouroEstadoProprietario]: {
              value: address?.uf ?? "",
              validations: [{required: true}],
            }
          };
        } else {
          newFormData = {
            ...formData,
            [ids.cepDefensor]: {
              value: formatted,
              validations: [],
            },
            [ids.logradouroDefensor]: {
              value: address?.logradouro ?? "",
              validations: [],
            },
            [ids.logradouroBairroDefensor]: {
              value: address?.bairro ?? "",
              validations: [],
            },
            [ids.logradouroMunicipioDefensor]: {
              value: address?.localidade ?? "",
              validations: [],
            },
            [ids.logradouroEstadoDefensor]: {
              value: address?.uf ?? "",
              validations: [],
            }
          };
        }
        setFormData(newFormData);
        validateErrors(newFormData);
      }
    }
  };

  const toFormData = () => {
    const formValues = {
      placa_veiculo: formData[ids.placaVeiculo].value.trim(),
      uf_placa_veiculo: formData[ids.ufPlaca].value.trim(),
      descricao_marca_modelo_veiculo: formData[ids.veiculoMarcaModelo].value.trim(),
      ano_fabricacao_veiculo: formData[ids.veiculoAnoFab].value,
      ano_modelo_veiculo: formData[ids.veiculoAnoModelo].value,
      descricao_cor_veiculo: formData[ids.corVeiculo].value.trim(),
      numero_chassi_veiculo: formData[ids.chassiVeiculo].value.trim(),
      numero_renavam_veiculo: formData[ids.renavamVeiculo].value.trim(),
      marca_taximetro: formData[ids.marcaTaximetro].value.trim(),
      modelo_taximetro: formData[ids.modeloTaximetro].value.trim(),
      numero_taximetro: formData[ids.numeroTaximetro].value.trim(),
      marca_tacografo: formData[ids.marcaTacografo].value.trim(),
      modelo_tacografo: formData[ids.modeloTacografo].value.trim(),
      numero_tacografo: formData[ids.numeroTacografo].value.trim(),
      data_inicio_atividade: formData[ids.dataInicioAtividade].value.split('-').reverse().join('/'),
      observacoes: formData[ids.observacoes].value.trim(),
      id_ponto_permissionario: formData[ids.pracaTaxi].value,
      numero: formData[ids.pontoTaxi].value.trim(),
      historico: formData[ids.motivoEdicao].value.trim(),
      inscricao_municipal: formData[ids.inscricaoMunicipal].value.trim(),
      atividade_remunerada: formData[ids.atividadeRemunerada].value,
      nome_proprietario: formData[ids.nomeProprietario].value.trim(),
      apelido_proprietario: formData[ids.apelidoProprietario].value.trim(),
      nome_social_proprietario: formData[ids.nomeSocialProprietario].value.trim(),
      sexo_proprietario: formData[ids.sexoProprietario].value,
      data_nascimento_proprietario: formData[ids.dataNascProprietario].value.split('-').reverse().join('/'),
      identidade_proprietario: formData[ids.identidadeProprietario].value.trim(),
      orgao_expedidor_identidade_proprietario: formData[ids.orgaoExpedidorIdentidadeProprietario].value.trim(),
      uf_identidade_proprietario: formData[ids.ufIdentidadeProprietario].value.trim(),
      cpf_cnpj_proprietario: formData[ids.cpfCnpjProprietario].value.trim(),
      cnh_proprietario: formData[ids.cnhProprietario].value.trim(),
      categoria_cnh_proprietario: formData[ids.cnhProprietarioCategoria].value.trim(),
      validade_cnh_proprietario: formData[ids.cnhProprietarioValidade].value.split('-').reverse().join('/'),
      logradouro_proprietario: ownerAddress.logradouro || formData[ids.logradouroProprietario].value.trim(),
      numero_proprietario: formData[ids.logradouroNumeroProprietario].value.trim(),
      bairro_proprietario: ownerAddress.bairro || formData[ids.logradouroBairroProprietario].value.trim(),
      municipio_proprietario: ownerAddress.localidade || formData[ids.logradouroNumeroProprietario].value.trim(),
      uf_proprietario: ownerAddress.uf || formData[ids.logradouroEstadoProprietario].value.trim(),
      cep_proprietario: formData[ids.cepProprietario].value.trim(),
      telefone_fixo_proprietario: formData[ids.telefoneFixoProprietario].value.trim(),
      telefone_celular_proprietario: formData[ids.telefoneCelularProprietario].value.trim(),
      nome_defensor: formData[ids.nomeDefensor].value.trim(),
      nome_social_defensor: formData[ids.nomeSocialDefensor].value.trim(),
      sexo_defensor: formData[ids.sexoDefensor].value,
      apelido_defensor: formData[ids.apelidoDefensor].value.trim(),
      data_nascimento_defensor: formData[ids.dataNascDefensor].value.split('-').reverse().join('/'),
      identidade_defensor: formData[ids.identidadeDefensor].value.trim(),
      orgao_expedidor_identidade_defensor: formData[ids.orgaoExpedidorIdentidadeDefensor].value.trim(),
      uf_identidade_defensor: formData[ids.ufIdentidadeDefensor].value.trim(),
      cpf_cnpj_defensor: formData[ids.cpfCnpjDefensor].value.trim(),
      cnh_defensor: formData[ids.cnhDefensor].value.trim(),
      categoria_cnh_defensor: formData[ids.cnhDefensorCategoria].value.trim(),
      validade_cnh_defensor: formData[ids.cnhDefensorValidade].value.split('-').reverse().join('/'),
      logradouro_defensor: defenderAddress.logradouro || formData[ids.logradouroDefensor].value.trim(),
      numero_defensor: formData[ids.logradouroNumeroDefensor].value.trim(),
      bairro_defensor: defenderAddress.bairro || formData[ids.logradouroBairroDefensor].value.trim(),
      municipio_defensor: defenderAddress.localidade || formData[ids.logradouroMunicipioDefensor].value.trim(),
      uf_defensor: defenderAddress.uf || formData[ids.logradouroEstadoDefensor].value.trim(),
      cep_defensor: formData[ids.cepDefensor].value.trim(),
      telefone_fixo_defensor: formData[ids.telefoneFixoDefensor].value.trim(),
      telefone_celular_defensor: formData[ids.telefoneCelularDefensor].value.trim(),
    };

    const data = new FormData();

    data.append("requestData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));
    if (attachmentRef?.current?.getFile()) {
      data.append("foto", attachmentRef.current.getFile().file, attachmentRef.current.getFile().name);
    }
    if (croppedImage){
      data.append("webcam", croppedImage);
    }

    return data;
  };

  const handleSave = async () => {
    try {
      const data = toFormData();
      if (isEdit) {
        await permitteesService.update(id, data);
        showToast({
          title: t("permittees.messages.success.update"), status: "success",
        });
        redirectToList();
      } else {
        await permitteesService.create(data);
        showToast({
          title: t(`permittees.messages.success.${isCopy ? 'copy' : 'create'}`), status: "success",
        });
        redirectToList();
      }
    } catch (err) {
      showToast({title: err.message ? t(err.message) : t(err) , status: "error"});
    }
  };

  const isFormInvalid = () => {
    return formData && inputError ? hasError({formData, inputError}) : true;
  }

  const redirectToList = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.permittee);
  };

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;

    let maskedValue = maskMap[id] ? maskMap[id](value) : value;

    if (type === "checkbox") {
      maskedValue = checked;
    }

    const newFormData = {
      ...formData, [id]: {...formData[id], value: maskedValue},
    };

    setFormData(newFormData);

    if (id === ids.pracaTaxi) {
      setSelectedStand(value);
      fetchCurrentStand(value)
    }

    validateErrors(newFormData, id, maskedValue);
  };

  const validateErrors = (newFormData, id = null , maskedValue = null) => {
    const validator = validatorMap[id];
    let formValidationErrors = checkErrors({ formData: newFormData, inputError, name: id })

    if (maskedValue === null) {
      delete formValidationErrors[id];
    } else if (validator) {
      if (!validator.validate(maskedValue)) {
        formValidationErrors[id] = t(`${translationKey}.messages.errors.${validator.errorKey}`);
      } else {
        delete formValidationErrors[id];
      }
    }
    setInputError(formValidationErrors);
  }

  const isStandValid = !!selectedStand;

  const handleVehiclePlateSearch = async () => {
    try {
      const result = await vehicleService.findByPlate(formData[ids.placaVeiculo].value);
      const veiculoData = {
        [ids.veiculoAnoFab]: {
          ...formData[ids.veiculoAnoFab],
          value: parseInt(result.anoFabricacao, 10),
        },
        [ids.veiculoAnoModelo]: {
          ...formData[ids.veiculoAnoModelo],
          value: parseInt(result.anoModelo, 10),
        },
        [ids.renavamVeiculo]: {
          ...formData[ids.renavamVeiculo],
          value: result.numeroRenavam,
        },
        [ids.corVeiculo]: {
          ...formData[ids.corVeiculo],
          value: result.descricaoCor,
        },
        [ids.chassiVeiculo]: {
          ...formData[ids.chassiVeiculo],
          value: result.numeroChassi,
        },
        [ids.veiculoMarcaModelo]: {
          ...formData[ids.veiculoMarcaModelo],
          value: result.descricaoMarcaModelo,
        },
        [ids.ufPlaca]: {
          ...formData[ids.ufPlaca],
          value: result.ufPlaca,
        },
        [ids.veiculoAnoModelo]: {
          ...formData[ids.veiculoAnoModelo],
          value: result.anoModelo,
        },
        [ids.nomeProprietario]: {
          ...formData[ids.nomeProprietario],
          value: result.nomeProprietario,
        },
      };

      setFormData((prev) => ({...prev, ...veiculoData}));
      setInputError(checkErrors({formData: veiculoData, inputError}));
    } catch (err) {
      showToast({title: t(err), status: "error"});
    }
  };

  const fetchStands = async () => {
    try {
      const res = await permitteesPointsService.findMany();
      setPracas(res ?? []);
    } catch (error) {
      setPracas([]);
    }
  };

  const fetchCurrentStand = async (id) => {
    try {
      const res = await permitteesPointsService.find(id);
      setSelectedStand(res.id ?? null);
      setVehicleType(res.tipo ?? null);
    } catch (error) {
      setSelectedStand(null);
    }
  };

  const handleSaveStand = async (selected, data) => {
    try {
      let stand;
      if (selectedStand) {
        await permitteesPointsService.update(selected, data);
        stand = {...selectedStand, ...data, "id" : selected};
        showToast({
          title: t("permittees.taxi-stand.messages.success.update"), status: "success",
        });
      } else {
        stand = await permitteesPointsService.create(data);
        showToast({
          title: t("permittees.taxi-stand.messages.success.create"), status: "success",
        });
      }

      await fetchStands();

      fetchCurrentStand(stand.id)
      setSelectedStand(stand.id);
      const { [ids.pracaTaxi]: removed, ...errors } = inputError;
      setInputError(errors);

      setFormData((prevData) => ({
        ...prevData, stand: {
          ...prevData.stand, value: stand.pracaTaxi,
        },
      }));

      setInputError((prevError) => {
        const newErrors = {...prevError};
        delete newErrors.stand;
        return newErrors;
      });

      onCloseStand();
    } catch (err) {
      showToast({title: t(err), status: "error"});
    }
  };

  const handleConfirmDeleteAttachment  = async () => {
    if (id || copy_id) {
      try {
        await permitteesService.removeAttachment(id ? id : copy_id);
        fetchData();
      } catch (err) {
        showToast({ title: t(err.message), status: "error" });
      }
    }
  };

  const fetchData = async () => {
    try {
      if (id || copy_id) {
        const data = await permitteesService.find(id || copy_id);
        const newFormData = initFormData(data);
        setFormData(newFormData);
        fetchCurrentStand(newFormData[ids.pracaTaxi].value);
        try {
          const attachment = await permitteesService.attachment(id ? id : copy_id);
          setInitialAttachmentFile(blobToFile(attachment, id ? id : copy_id));
        } catch (err) {
          setInitialAttachmentFile(null);
        }
      } else {
        setFormData(initFormData());
      }

    } catch (error) {
      showToast({ title: t(error), status: "error" });
      redirectToList();
    }
  }

  useEffect(() => {
    if (selectedStand !== null) {
      setFormData(prevData => ({
        ...prevData,
        [ids.pracaTaxi]: {
          ...prevData[ids.pracaTaxi],
          value: selectedStand
        }
      }));
    }
  }, [selectedStand]);


  useEffect(() => {
    fetchStands();
    fetchData();

    if ((isEdit || isCopy) && !loading && initialData) {
      return;
    }

  }, [loading, initialData]);

  return (<Flex gap="5" direction="column" minH="89vh" align="center" pt={{sm: "150px", md: "90px"}}>

      {/*Vehicle card*/}
      <Card overflowX={{sm: "scroll", lg: "hidden"}}>
        <CardHeader mb="20px">
          <Text color={textColor} fontSize="lg" fontWeight="bold">
            {t(`${formTranslationKey}.title.vehicle-data`)}
          </Text>
        </CardHeader>
        <CardBody>
          {formData && !loading ? (<Stack w="100%" direction="column" spacing={cardSpacing}>
              {/*vehicle-plate*/}
              <Stack alignSelf="flex-start" spacing={formSpacing} direction="row" minW="50%" alignItems="flex-start">
                <FormControl isRequired isInvalid={inputError[ids.placaVeiculo]}>
                  <Input
                    fontSize="sm"
                    id={ids.placaVeiculo}
                    name={ids.placaVeiculo}
                    value={formData[ids.placaVeiculo].value}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.vehicle-plate.placeholder`)}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.placaVeiculo])}
                  </FormErrorMessage>
                </FormControl>

                <LightMode>
                  <Button
                    variant="dark"
                    fontSize="sm"
                    isDisabled={inputError[ids.placaVeiculo] || !formData[ids.placaVeiculo].value}
                    leftIcon={<SearchIcon/>}
                    onClick={handleVehiclePlateSearch}
                    minW="fit-content"
                    _hover={isFormInvalid() ? {} : undefined}
                  >
                    <Text>
                      {t(`${formTranslationKey}.fields.vehicle-plate.label`)}
                    </Text>
                  </Button>
                </LightMode>
              </Stack>

              <Stack spacing={formSpacing} direction={formDirection}>
                {/*vehicle-uf-plate*/}
                <FormControl isInvalid={inputError[ids.ufPlaca]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-uf-plate.label`)}
                  </FormLabel>
                  <Select
                      fontSize="sm"
                      variant="main"
                      id={ids.ufPlaca}
                      placeholder={t(`${formTranslationKey}.fields.id-uf.placeholder`)}
                      value={formData[ids.ufPlaca].value.toUpperCase()}
                      onChange={handleInputChange}
                  >
                    {ufs.map((uf) => (
                        <option key={uf.value} value={uf.value}>
                          {uf.value} - {uf.label}
                        </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {t(inputError[ids.ufPlaca])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-model*/}
                <FormControl isInvalid={inputError[ids.veiculoMarcaModelo]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-model.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    id={ids.veiculoMarcaModelo}
                    value={formData[ids.veiculoMarcaModelo].value}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.veiculoMarcaModelo])}
                  </FormErrorMessage>
                </FormControl>

                {/*vehicle-fabrication-year*/}
                <YearMaskedInput
                    id={ids.veiculoAnoFab}
                    label={`${formTranslationKey}.fields.vehicle-fabrication-year.label`}
                    value={formData[ids.veiculoAnoFab].value}
                    onChange={handleInputChange}
                    error={inputError[ids.veiculoAnoFab]}
                />

                {/*vehicle-model-year*/}
                <YearMaskedInput
                  id={ids.veiculoAnoModelo}
                  label={`${formTranslationKey}.fields.vehicle-model-year.label`}
                  value={formData[ids.veiculoAnoModelo].value}
                  onChange={handleInputChange}
                  error={inputError[ids.veiculoAnoModelo]}
                />
              </Stack>

              <Stack spacing={formSpacing} direction={formDirection}>
                {/*cor_veiculo*/}
                <FormControl isInvalid={inputError[ids.corVeiculo]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-color.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    id={ids.corVeiculo}
                    value={formData[ids.corVeiculo].value}
                    onChange={handleInputChange}
                  />
                </FormControl>

                {/*chassi_veiculo*/}
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-chassis.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    id={ids.chassiVeiculo}
                    value={formData[ids.chassiVeiculo].value}
                    onChange={handleInputChange}
                  />
                </FormControl>

                {/*renavam_veiculo*/}
                <FormControl isInvalid={inputError[ids.renavamVeiculo]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.vehicle-renavam.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    id={ids.renavamVeiculo}
                    value={formData[ids.renavamVeiculo].value}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </Stack>

              {/*taximetro*/}
              {(isEdit || isCopy) && vehicleType === pointTypeList.TAXI.value && (<Stack spacing={formSpacing} direction={formDirection}>
                  {/*marca_taximetro*/}
                  <FormControl isInvalid={inputError[ids.marcaTaximetro]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.taximeter-brand.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      id={ids.marcaTaximetro}
                      value={formData[ids.marcaTaximetro].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/*modelo_taximetro*/}
                  <FormControl isInvalid={inputError[ids.modeloTaximetro]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.taximeter-model.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      id={ids.modeloTaximetro}
                      value={formData[ids.modeloTaximetro].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/*numero_taximetro*/}
                  <FormControl isInvalid={inputError[ids.numeroTaximetro]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.taximeter-number.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      id={ids.numeroTaximetro}
                      value={formData[ids.numeroTaximetro].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </Stack>)}

              {/*tacografo*/}
              {(isEdit || isCopy) && vehicleType !== pointTypeList.TAXI.value && (
                <Stack spacing={formSpacing} direction={formDirection}>
                  {/*marca_tacografo*/}
                  <FormControl isInvalid={inputError[ids.marcaTacografo]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.tachograph-brand.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      id={ids.marcaTacografo}
                      value={formData[ids.marcaTacografo].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/*modelo_tacografo*/}
                  <FormControl isInvalid={inputError[ids.modeloTacografo]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.tachograph-model.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      id={ids.modeloTacografo}
                      value={formData[ids.modeloTacografo].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/*numero_tacografo*/}
                  <FormControl isInvalid={inputError[ids.numeroTacografo]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.tachograph-number.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      id={ids.numeroTacografo}
                      value={formData[ids.numeroTacografo].value}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </Stack>)}
            </Stack>) : (<Loading h="300px" w="100%"/>)}
        </CardBody>
      </Card>

      <Stack w="100%" direction={{sm: "column", md: "row"}} spacing={cardSpacing}>
        {/*Permittee card*/}
        <Card flex="1" minW="0" overflowX={{sm: "scroll", lg: "hidden"}} minH="49vh">
          <CardHeader mb="20px">
            <Text color={textColor} fontSize="lg" fontWeight="bold">
              {t(`${formTranslationKey}.title.permittee-data`)}
            </Text>
          </CardHeader>
          <CardBody>
            {formData && !loading ? (<Stack direction="column">

                {/*praca*/}
                <FormControl isRequired isInvalid={inputError[ids.pracaTaxi]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.taxi-stand.label`)}
                  </FormLabel>
                  <HStack align="center" justify="space-between">
                    <SearchSelect
                      id={ids.pracaTaxi}
                      value={formData[ids.pracaTaxi].value}
                      items={pracas}
                      displayField={["numero", "tipo", "logradouro"]}
                      valueField="id"
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.pracaTaxi])}
                    </FormErrorMessage>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={onOpenStand}
                    >
                      {isStandValid ? <EditIcon/> : <AddIcon/>}
                    </Button>
                  </HStack>
                </FormControl>

                {/*ponto*/}
                <FormControl isInvalid={inputError[ids.pontoTaxi]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.taxi-point.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    id={ids.pontoTaxi}
                    value={formData[ids.pontoTaxi].value}
                    onChange={handleInputChange}
                  />
                </FormControl>

                {/*inscricao municipal*/}
                <FormControl isInvalid={inputError[ids.inscricaoMunicipal]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.city-registration-number.label`)}
                  </FormLabel>
                  <Input
                    fontSize="sm"
                    id={ids.inscricaoMunicipal}
                    value={formData[ids.inscricaoMunicipal].value}
                    onChange={handleInputChange}
                  />
                </FormControl>

                {/*data inicio atividade*/}
                <FormControl isRequired isInvalid={!!inputError[ids.dataInicioAtividade]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.activity-initial-date.label`)}
                  </FormLabel>
                  <DateTime
                    dateFormat={DATE_FORMAT}
                    timeFormat={false}
                    locale={LOCALE_PT_BR}
                    value={
                      formData[ids.dataInicioAtividade].value
                        ? moment(formData[ids.dataInicioAtividade].value, DATE_FORMAT_ISO)
                        : ""
                    }
                    renderInput={(props) => (
                      <Input
                        {...props}
                        fontSize="sm"
                        variant="main"
                        id={ids.dataInicioAtividade}
                      />
                    )}
                    onChange={(date) =>
                      handleInputChange({
                        target: {
                          id: ids.dataInicioAtividade,
                          value: moment(date).isValid()
                            ? moment(date).format(DATE_FORMAT_ISO)
                            : "",
                        },
                      })
                    }
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.dataInicioAtividade])}
                  </FormErrorMessage>
                </FormControl>

                {/*atividade remunerada*/}
                <FormControl isInvalid={inputError[ids.atividadeRemunerada]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.paid-activity.label`)}
                  </FormLabel>
                  <Switch
                    id={ids.atividadeRemunerada}
                    isChecked={parseBoolean(formData[ids.atividadeRemunerada].value)}
                    onChange={handleInputChange}
                  />
                </FormControl>

                {/*observacoes*/}
                <FormControl isInvalid={inputError[ids.observacoes]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.observations.label`)}
                  </FormLabel>
                  <Textarea
                    fontSize="sm"
                    variant="main"
                    id={ids.observacoes}
                    value={formData[ids.observacoes].value}
                    onChange={handleInputChange}
                  />
                </FormControl>

                {/*motivo edicao*/}
                {(isEdit) && (
                <FormControl isRequired isInvalid={inputError[ids.motivoEdicao]}>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.reason-of-update.label`)}
                  </FormLabel>
                  <Textarea
                    fontSize="sm"
                    variant="main"
                    id={ids.motivoEdicao}
                    value={formData[ids.motivoEdicao].value}
                    onChange={handleInputChange}
                  />
                </FormControl>
                )}

                {/*foto*/}
                <FileAttachmentInput
                  ref={attachmentRef}
                  id={ids.foto}
                  nameVariable={fileAttachmentName}
                  acceptedTypes={['image/jpeg']}
                  translationKey={translationKey + '.form'}
                  initialFile={initialAttachmentFile}
                  onConfirmDelete={handleConfirmDeleteAttachment}
                />

                {/*webcam*/}
                <LightMode>
                  <FormLabel fontSize="xs" fontWeight="bold">
                    {t(`${formTranslationKey}.fields.webcam.label`)}
                  </FormLabel>

                  <WebcamCapture onCapture={handleImageCaptured}/>
                </LightMode>

              </Stack>) : (<Loading h="300px" w="100%"/>)}
          </CardBody>

          {/*modal praca*/}
          <PermitteesTaxiStandModal
            isOpen={isOpenStand}
            onClose={onCloseStand}
            selectedStand={selectedStand}
            stands={pracas}
            onSave={handleSaveStand}
            isEdit={isStandValid}
          />
        </Card>

        <Card flex="1" minW="0" overflowX={{sm: "scroll", lg: "hidden"}} minH="49vh">
          <Tabs  minW="0" isFitted variant="soft-rounded">
              <TabList mb="1em" gap={2}>
                  <Tab
                    _selected={{ bg: useColorModeValue("gray.200", "gray.600") }}
                    border="1px solid"
                    borderColor={useColorModeValue("gray.200", "navy.600")}
                    _hover={{ bg: useColorModeValue("gray.100", "gray.500") }}
                  >
                    <Text color={textColor} fontWeight="bold">
                      {t(`${formTranslationKey}.title.owner`)}
                    </Text>
                  </Tab>
                  <Tab
                    _selected={{ bg: useColorModeValue("gray.200", "gray.600") }}
                    border="1px solid"
                    borderColor={useColorModeValue("gray.200", "navy.600")}
                    _hover={{ bg: useColorModeValue("gray.100", "gray.500") }}
                  >
                    <Text color={textColor} fontWeight="bold">
                      {t(`${formTranslationKey}.title.defender`)}
                    </Text>
                  </Tab>
                </TabList>
              <TabPanels>
                <TabPanel padding={0}>
                    <CardBody>
                      {formData && !loading ? (<Stack direction="column">
                          {/*nome_proprietario*/}
                          <FormControl isRequired isInvalid={!!inputError[ids.nomeProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.name.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.nomeProprietario}
                              value={formData[ids.nomeProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.nomeProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*nome_social_proprietario*/}
                          <FormControl isInvalid={!!inputError[ids.nomeSocialProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.social-name.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.nomeSocialProprietario}
                              value={formData[ids.nomeSocialProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.nomeSocialProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*apelido_proprietario*/}
                          <FormControl isInvalid={!!inputError[ids.apelidoProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.alias.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.apelidoProprietario}
                              value={formData[ids.apelidoProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.apelidoProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*sexo_proprietario*/}
                          <FormControl isInvalid={!!inputError[ids.sexoProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.gender.label`)}
                            </FormLabel>
                            <Select
                              fontSize="sm"
                              id={ids.sexoProprietario}
                              value={formData[ids.sexoProprietario].value}
                              onChange={handleInputChange}
                            >
                              <option></option>
                              {genderList?.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {t(`${formTranslationKey}.fields.gender.options.${option.labelKey}`)}
                                </option>)
                              )}
                            </Select>
                            <FormErrorMessage>
                              {t(inputError[ids.sexoProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*data_nasc_proprietario*/}
                          <FormControl isRequired isInvalid={!!inputError[ids.dataNascProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.birth.label`)}
                            </FormLabel>
                            <DateTime
                              dateFormat={DATE_FORMAT}
                              timeFormat={false}
                              locale={LOCALE_PT_BR}
                              value={
                                formData[ids.dataNascProprietario].value
                                  ? moment(formData[ids.dataNascProprietario].value, DATE_FORMAT_ISO)
                                  : ""
                              }
                              renderInput={(props) => (
                                <Input
                                  {...props}
                                  fontSize="sm"
                                  variant="main"
                                  id={ids.dataNascProprietario}
                                />
                              )}
                              onChange={(date) =>
                                handleInputChange({
                                  target: {
                                    id: ids.dataNascProprietario,
                                    value: moment(date).isValid()
                                      ? moment(date).format(DATE_FORMAT_ISO)
                                      : "",
                                  },
                                })
                              }
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.dataNascProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*num_identidade_proprietario*/}
                          <FormControl isInvalid={inputError[ids.identidadeProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.id.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.identidadeProprietario}
                              value={formData[ids.identidadeProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.identidadeProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*orgao_expedidor_identidade_proprietario*/}
                          <FormControl isInvalid={inputError[ids.orgaoExpedidorIdentidadeProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.id-expeditioner.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.orgaoExpedidorIdentidadeProprietario}
                              value={formData[ids.orgaoExpedidorIdentidadeProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.orgaoExpedidorIdentidadeProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*uf_identidade_proprietario*/}
                          <FormControl isInvalid={inputError[ids.ufIdentidadeProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.id-uf.label`)}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                variant="main"
                                id={ids.ufIdentidadeProprietario}
                                placeholder={t(`${formTranslationKey}.fields.id-uf.placeholder`)}
                                value={formData[ids.ufIdentidadeProprietario].value.toUpperCase()}
                                onChange={handleInputChange}
                            >
                              {ufs.map((uf) => (
                                  <option key={uf.value} value={uf.value}>
                                    {uf.value} - {uf.label}
                                  </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {t(inputError[ids.ufIdentidadeProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cpf_proprietario*/}
                          <FormControl isRequired isInvalid={inputError[ids.cpfCnpjProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cpfcnpj.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cpfCnpjProprietario}
                              maxLength="18"
                              value={formData[ids.cpfCnpjProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cpfCnpjProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cnh_proprietario*/}
                          <FormControl isInvalid={inputError[ids.cnhProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cnh.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cnhProprietario}
                              value={formData[ids.cnhProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cnhProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*categoria_cnh_proprietario*/}
                          <FormControl isInvalid={inputError[ids.cnhProprietarioCategoria]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cnh-category.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cnhProprietarioCategoria}
                              value={formData[ids.cnhProprietarioCategoria].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cnhProprietarioCategoria])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*validade_cnh_proprietario*/}
                          <FormControl isInvalid={inputError[ids.cnhProprietarioValidade]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cnh-valid.label`)}
                            </FormLabel>
                            <DateTime
                              dateFormat={DATE_FORMAT}
                              timeFormat={false}
                              locale={LOCALE_PT_BR}
                              value={
                                formData[ids.cnhProprietarioValidade].value
                                  ? moment(formData[ids.cnhProprietarioValidade].value, DATE_FORMAT_ISO)
                                  : ""
                              }
                              renderInput={(props) => (
                                <Input
                                  {...props}
                                  fontSize="sm"
                                  variant="main"
                                  id={ids.cnhProprietarioValidade}
                                />
                              )}
                              onChange={(date) =>
                                handleInputChange({
                                  target: {
                                    id: ids.cnhProprietarioValidade,
                                    value: moment(date).isValid()
                                      ? moment(date).format(DATE_FORMAT_ISO)
                                      : "",
                                  },
                                })
                              }
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cnhProprietarioValidade])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cep_proprietario*/}
                          <FormControl isRequired isInvalid={!!inputError[ids.cepProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cep.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cepProprietario}
                              maxLength={9}
                              value={formData[ids.cepProprietario].value}
                              onChange={handleCEPChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cepProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*logradouro_proprietario*/}
                          <FormControl isRequired isInvalid={inputError[ids.logradouroProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.street.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroProprietario}
                              value={formData[ids.logradouroProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*numero_proprietario*/}
                          <FormControl isRequired isInvalid={inputError[ids.logradouroNumeroProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.street-number.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroNumeroProprietario}
                              value={formData[ids.logradouroNumeroProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroNumeroProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*bairro_proprietario*/}
                          <FormControl isRequired isInvalid={inputError[ids.logradouroBairroProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.address-neighbourhood.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroBairroProprietario}
                              value={formData[ids.logradouroBairroProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroBairroProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cidade_proprietario*/}
                          <FormControl isRequired isInvalid={inputError[ids.logradouroMunicipioProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.address-city.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroMunicipioProprietario}
                              value={formData[ids.logradouroMunicipioProprietario].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroMunicipioProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*uf_proprietario*/}
                          <FormControl isRequired isInvalid={inputError[ids.logradouroEstadoProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.address-state.label`)}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                variant="main"
                                id={ids.logradouroEstadoProprietario}
                                placeholder={t(`${formTranslationKey}.fields.address-state.placeholder`)}
                                value={formData[ids.logradouroEstadoProprietario].value.toUpperCase()}
                                onChange={handleInputChange}
                            >
                              {ufs.map((uf) => (
                                  <option key={uf.value} value={uf.value}>
                                    {uf.value} - {uf.label}
                                  </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroEstadoProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*telefone_fixo_proprietario*/}
                          <FormControl isInvalid={!!inputError[ids.telefoneFixoProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.landline.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.telefoneFixoProprietario}
                              value={formData[ids.telefoneFixoProprietario].value}
                              onChange={handleInputChange}
                              maxLength={15}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.telefoneFixoProprietario])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*telefone_celular_proprietario*/}
                          <FormControl isInvalid={!!inputError[ids.telefoneCelularProprietario]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cellphone.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.telefoneCelularProprietario}
                              value={formData[ids.telefoneCelularProprietario].value}
                              onChange={handleInputChange}
                              maxLength={15}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.telefoneCelularProprietario])}
                            </FormErrorMessage>
                          </FormControl>
                        </Stack>
                      ) : (
                        <Loading h="300px" w="100%"/>
                      )}
                    </CardBody>
                </TabPanel>
                <TabPanel padding={0}>
                    <CardBody>
                      {formData && !loading ? (
                        <Stack direction="column">
                          {/*nome_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.nomeDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.name.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.nomeDefensor}
                              value={formData[ids.nomeDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.nomeDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*nome_social_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.nomeSocialDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.social-name.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.nomeSocialDefensor}
                              value={formData[ids.nomeSocialDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.nomeSocialDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*apelido_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.apelidoDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.alias.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.apelidoDefensor}
                              value={formData[ids.apelidoDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.apelidoDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*sexo_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.sexoDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.gender.label`)}
                            </FormLabel>
                            <Select
                              fontSize="sm"
                              id={ids.sexoDefensor}
                              value={formData[ids.sexoDefensor].value}
                              onChange={handleInputChange}
                            >
                              <option></option>
                              {genderList?.map((option, index) => (<option key={index} value={option.value}>
                                  {t(`${formTranslationKey}.fields.gender.options.${option.labelKey}`)}
                                </option>))}
                            </Select>
                            <FormErrorMessage>
                              {t(inputError[ids.sexoDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*data_nasc_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.dataNascDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.birth.label`)}
                            </FormLabel>
                            <DateTime
                              dateFormat={DATE_FORMAT}
                              timeFormat={false}
                              locale={LOCALE_PT_BR}
                              value={
                                formData[ids.dataNascDefensor].value
                                  ? moment(formData[ids.dataNascDefensor].value, DATE_FORMAT_ISO)
                                  : ""
                              }
                              renderInput={(props) => (
                                <Input
                                  {...props}
                                  fontSize="sm"
                                  variant="main"
                                  id={ids.dataNascDefensor}
                                />
                              )}
                              onChange={(date) =>
                                handleInputChange({
                                  target: {
                                    id: ids.dataNascDefensor,
                                    value: moment(date).isValid()
                                      ? moment(date).format(DATE_FORMAT_ISO)
                                      : "",
                                  },
                                })
                              }
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.dataNascDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*num_identidade_defensor*/}
                          <FormControl isInvalid={inputError[ids.identidadeDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.id.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.identidadeDefensor}
                              value={formData[ids.identidadeDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.identidadeDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*orgao_expedidor_identidade_defensor*/}
                          <FormControl isInvalid={inputError[ids.orgaoExpedidorIdentidadeDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.id-expeditioner.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.orgaoExpedidorIdentidadeDefensor}
                              value={formData[ids.orgaoExpedidorIdentidadeDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.orgaoExpedidorIdentidadeDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*uf_identidade_defensor*/}
                          <FormControl isInvalid={inputError[ids.ufIdentidadeDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.id-uf.label`)}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                variant="main"
                                id={ids.ufIdentidadeDefensor}
                                placeholder={t(`${formTranslationKey}.fields.id-uf.placeholder`)}
                                value={formData[ids.ufIdentidadeDefensor].value.toUpperCase()}
                                onChange={handleInputChange}
                            >
                              {ufs.map((uf) => (
                                  <option key={uf.value} value={uf.value}>
                                    {uf.value} - {uf.label}
                                  </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {t(inputError[ids.ufIdentidadeDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cpf_defensor*/}
                          <FormControl isInvalid={inputError[ids.cpfCnpjDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cpfcnpj.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cpfCnpjDefensor}
                              maxLength="18"
                              value={formData[ids.cpfCnpjDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cpfCnpjDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cnh_defensor*/}
                          <FormControl isInvalid={inputError[ids.cnhDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cnh.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cnhDefensor}
                              value={formData[ids.cnhDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cnhDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*categoria_cnh_defensor*/}
                          <FormControl isInvalid={inputError[ids.cnhDefensorCategoria]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cnh-category.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cnhDefensorCategoria}
                              value={formData[ids.cnhDefensorCategoria].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cnhDefensorCategoria])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*validade_cnh_defensor*/}
                          <FormControl isInvalid={inputError[ids.cnhDefensorValidade]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cnh-valid.label`)}
                            </FormLabel>
                            <DateTime
                              dateFormat={DATE_FORMAT}
                              timeFormat={false}
                              locale={LOCALE_PT_BR}
                              value={
                                formData[ids.cnhDefensorValidade].value
                                  ? moment(formData[ids.cnhDefensorValidade].value, DATE_FORMAT_ISO)
                                  : ""
                              }
                              renderInput={(props) => (
                                <Input
                                  {...props}
                                  fontSize="sm"
                                  variant="main"
                                  id={ids.cnhDefensorValidade}
                                />
                              )}
                              onChange={(date) =>
                                handleInputChange({
                                  target: {
                                    id: ids.cnhDefensorValidade,
                                    value: moment(date).isValid()
                                      ? moment(date).format(DATE_FORMAT_ISO)
                                      : "",
                                  },
                                })
                              }
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cnhDefensorValidade])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cep_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.cepDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cep.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.cepDefensor}
                              maxLength={9}
                              value={formData[ids.cepDefensor].value}
                              onChange={handleCEPChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.cepDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*logradouro_defensor*/}
                          <FormControl isInvalid={inputError[ids.logradouroDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.street.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroDefensor}
                              value={formData[ids.logradouroDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*numero_defensor*/}
                          <FormControl isInvalid={inputError[ids.logradouroNumeroDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.street-number.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroNumeroDefensor}
                              value={formData[ids.logradouroNumeroDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroNumeroDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*bairro_defensor*/}
                          <FormControl isInvalid={inputError[ids.logradouroBairroDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.address-neighbourhood.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroBairroDefensor}
                              value={formData[ids.logradouroBairroDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroBairroDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*cidade_defensor*/}
                          <FormControl isInvalid={inputError[ids.logradouroMunicipioDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.address-city.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.logradouroMunicipioDefensor}
                              value={formData[ids.logradouroMunicipioDefensor].value}
                              onChange={handleInputChange}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroMunicipioDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*uf_defensor*/}
                          <FormControl isInvalid={inputError[ids.logradouroEstadoDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.address-state.label`)}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                variant="main"
                                id={ids.logradouroEstadoDefensor}
                                placeholder={t(`${formTranslationKey}.fields.id-uf.placeholder`)}
                                value={formData[ids.logradouroEstadoDefensor].value.toUpperCase()}
                                onChange={handleInputChange}
                            >
                              {ufs.map((uf) => (
                                  <option key={uf.value} value={uf.value}>
                                    {uf.value} - {uf.label}
                                  </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {t(inputError[ids.logradouroEstadoDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*telefone_fixo_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.telefoneFixoDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.landline.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.telefoneFixoDefensor}
                              value={formData[ids.telefoneFixoDefensor].value}
                              onChange={handleInputChange}
                              maxLength={15}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.telefoneFixoDefensor])}
                            </FormErrorMessage>
                          </FormControl>

                          {/*telefone_celular_defensor*/}
                          <FormControl isInvalid={!!inputError[ids.telefoneCelularDefensor]}>
                            <FormLabel fontSize="xs" fontWeight="bold">
                              {t(`${formTranslationKey}.fields.cellphone.label`)}
                            </FormLabel>
                            <Input
                              fontSize="sm"
                              id={ids.telefoneCelularDefensor}
                              value={formData[ids.telefoneCelularDefensor].value}
                              onChange={handleInputChange}
                              maxLength={15}
                            />
                            <FormErrorMessage>
                              {t(inputError[ids.telefoneCelularDefensor])}
                            </FormErrorMessage>
                          </FormControl>
                        </Stack>
                      ) : (
                        <Loading h="300px" w="100%"/>
                      )}
                    </CardBody>
                </TabPanel>
              </TabPanels>
          </Tabs>
        </Card>
      </Stack>
      <Stack w="100%" direction="row" justify="space-between" >
        <Button w="125px" variant="no-effects" bg="gray.100" color="gray.700" fontSize="sm"
                fontWeight="bold" onClick={redirectToList}>
          <Text>
            {t("system.actions.cancel")}
          </Text>
        </Button>

        <Button
          w="125px"
          variant="dark"
          fontSize="sm"
          isDisabled={isFormInvalid()}
          onClick={handleSave}
          _hover={isFormInvalid() ? {} : undefined}
        >
          <Text>
            { isEdit ?
              t("system.actions.edit") :
              t("system.actions.save")
            }
          </Text>
        </Button>
      </Stack>
      <ConfirmDialog ref={dialogRef} confirmButtonProps={{colorScheme: "red"}}/>
    </Flex>);
}

export default PermitteesNew;
